import { COOKIES } from '~~/shared/constants/cookies'
import {
  ONE_HOUR_IN_SECONDS,
  ONE_WEEK_IN_SECONDS,
  ONE_YEAR_IN_SECONDS,
  SIXTY_DAYS_IN_SECONDS,
} from '#shared/constants/durations'

import type { HelpWizardCookie } from '~/components/search/help-widget-modal/types.ts'
import type { EXTERNAL_AUTHENTICATION_PROVIDERS } from '~/constants/authentication'
import type { StoredClickIds } from '~/types/click-ids'
import type { RequestToBookStep } from '~/types/request-to-book'
import type { Platform } from '~/types/platform'

/**
 * Cookie defaults. Can be overwritten per cookie.
 *
 * @returns Object with default cookie settings
 */
export function cookieDefaults() {
  const runtimeConfig = useRuntimeConfig()

  return {
    path: '/',
    maxAge: SIXTY_DAYS_IN_SECONDS,
    domain: runtimeConfig.public.cookieDomain,
  }
}

// Stores the auth intent.
export const useIntentCookie = () => useCookie(COOKIES.risIntent, {
  ...cookieDefaults(),
  maxAge: ONE_HOUR_IN_SECONDS,
})

// Stores the auth redirect.
export const useRedirectCookie = () => useCookie(COOKIES.risRedirect, {
  ...cookieDefaults(),
})

// Stores where the external auth was triggered from.
export const useAuthTypeCookie = () => useCookie(COOKIES.risAuthType, {
  ...cookieDefaults(),
  maxAge: ONE_HOUR_IN_SECONDS,
})

// Stores the RIS provider.
export const useRisProviderCookie = () => useCookie<Nullable<EXTERNAL_AUTHENTICATION_PROVIDERS>>(COOKIES.risProvider, {
  ...cookieDefaults(),
})

// Stores the redirect for the new RTB flow when completing a profile.
export const usePendingRequestToBookUrlCookie = () => useCookie(COOKIES.pendingRequestToBookUrl, {
  ...cookieDefaults(),
  maxAge: ONE_HOUR_IN_SECONDS,
})

// Stores the rtsession ID for the user.
export const useRtsessionIdCookie = () => useCookie(COOKIES.session, {
  ...cookieDefaults(),
  maxAge: useRuntimeConfig().public.sessionCookieTTL,
})

// Stores the user's email address.
export const useUserEmailCookie = () => useCookie(COOKIES.userEmail, {
  ...cookieDefaults(),
})

// Stores the access token.
export const useAccessTokenCookie = () => useCookie(COOKIES.accessToken, {
  ...cookieDefaults(),
})

// Stores the admin access token.
export const useAdminAccessTokenCookie = () => useCookie(COOKIES.adminAccessToken, {
  ...cookieDefaults(),
})

// Stores if the message templates tour has shown.
export const useMessageTemplateTourCookie = () => useCookie(COOKIES.hostMessageTemplatesTourChat, {
  ...cookieDefaults(),
  maxAge: ONE_YEAR_IN_SECONDS,
  default: () => 0,
})

// Stores wether or not the chat has been translated.
export const useChatTranslatedCookie = () => useCookie(COOKIES.chatTranslated, {
  ...cookieDefaults(),
  maxAge: ONE_YEAR_IN_SECONDS,
  default: () => false,
})

// Store the experiment overrides.
export const useExperimentOverridesCookie = () => useCookie(COOKIES.experimentOverrides, {
  ...cookieDefaults(),
})

// References the datadog cookie.
// No defaults as it's not our cookie.
export const useDatadogCookie = () => useCookie(COOKIES.datadog)

// Stores the path a user tried to reach while unauthorized.
export const useUnauthorizedRedirectCookie = () => useCookie(COOKIES.unauthorizedRedirect, {
  ...cookieDefaults(),
  maxAge: ONE_HOUR_IN_SECONDS,
})

// Reference sthe Google ID cookie.
// No defaults as it's not our cookie.
export const useGidCookie = () => useCookie(COOKIES.gid)

// Stored ad click IDs.
export const useClickIdsCookie = () => useCookie<StoredClickIds>(COOKIES.clickIds, {
  ...cookieDefaults(),
  maxAge: useRuntimeConfig().public.sessionCookieTTL,
  sameSite: 'lax',
  secure: !isMetaDev,
})

// Stores utm parameters.
export const useUtmParametersCookie = () => useCookie(COOKIES.utmParameters, {
  ...cookieDefaults(),
})

// Stores the used platform.
export const usePlatformCookie = () => useCookie<Nullable<Platform>>(COOKIES.platform, {
  ...cookieDefaults(),
})

// Stores mobile capabilities.
export const useCapabilitiesCookie = () => useCookie(COOKIES.capabilities, {
  ...cookieDefaults(),
})

// Stores wether we're running AT.
export const useAutomatedTestCookie = () => useCookie(COOKIES.automatedTest, {
  ...cookieDefaults(),
})

// Stores the progress in the search help wizard.
export const useSearchHelpWizard = () => useCookie<HelpWizardCookie>(COOKIES.searchHelpWizard, {
  ...cookieDefaults(),
})

// Stores the RTB progress for the user.
export const useRequestToBookStepCookie = () => useCookie<Record<string, RequestToBookStep>>(COOKIES.requestToBookStep, {
  maxAge: ONE_WEEK_IN_SECONDS,
  sameSite: 'lax',
  default: () => ({}),
})
